<script setup lang="ts">
import type { HomeType } from '~/composables/useHomeType'

/**
 * ExploreMapSectionV3 — annuaire régional unique de la home.
 *
 * Liste commune aux deux types (jamais dupliquée). Le rendu des compteurs suit
 * le type actif (spec-refonte-home-voyageur-switcher-type) :
 * - « Tout »  : 2 compteurs colorés côte à côte (sauge · ocre) + légende, là où
 *   le stock existe. La couleur ne va jamais sans le mot (légende + aria).
 * - filtré    : un seul compteur teinté du type actif. Le tri par densité du type
 *   est fait en amont dans index.vue.
 */
interface RegionWithSamples {
  slug: string
  name: string
  departmentSamples: string[]
  establishmentCount: number
  cdhCount: number
  giteCount: number
}

const props = withDefaults(defineProps<{
  regions: RegionWithSamples[]
  type?: HomeType
}>(), {
  type: 'all',
})

const localePath = useLocalePath()

const isAll = computed(() => props.type === 'all')
const isGite = computed(() => props.type === 'gite')

// Compteur affiché en mode filtré (un seul chiffre, teinté).
function filteredCount(region: RegionWithSamples): number {
  return isGite.value ? region.giteCount : region.cdhCount
}

// Le type actif de la home doit suivre le clic vers la page région, sinon
// l'utilisateur en mode gîte atterrit sur les chambres d'hôtes (défaut
// historique de la page région). Vocabulaire home (cdh/gite/all) → vocabulaire
// page région (chambre-hotes/gite/all).
const REGION_TYPE_PARAM: Record<HomeType, string> = {
  all: 'all',
  cdh: 'chambre-hotes',
  gite: 'gite',
}
function regionLink(region: RegionWithSamples) {
  return {
    path: localePath(`/regions/${region.slug}`),
    query: { type: REGION_TYPE_PARAM[props.type] },
  }
}
</script>

<template>
  <section id="explore-v3" class="py-20 md:py-28 bg-paper-50 text-paper-900">
    <div class="container mx-auto px-4 lg:px-8">
      <header class="max-w-2xl mb-10 md:mb-14">
        <p class="tag text-sage-700 mb-4">
          {{ $t('pages.home.exploreV3.tag') }}
        </p>
        <h2 class="font-display font-light tracking-[-0.02em] leading-tight text-3xl md:text-5xl text-paper-900 mb-4">
          {{ $t('pages.home.exploreV3.title') }}
        </h2>
        <p class="font-sans text-base md:text-lg text-paper-900/70 leading-relaxed">
          {{ $t('pages.home.exploreV3.subtitle') }}
        </p>
      </header>

      <!-- Légende des compteurs (mode « Tout » uniquement) — la couleur ne va
           jamais sans le mot. -->
      <div
        v-if="isAll"
        class="max-w-[1120px] mx-auto flex items-center justify-end gap-4 mb-3 font-mono text-[11px] uppercase tracking-[0.1em] text-paper-900/60"
      >
        <span class="inline-flex items-center gap-1.5">
          <span class="inline-block h-2 w-2 rounded-full" :style="{ backgroundColor: 'var(--color-type-cdh)' }" aria-hidden="true" />
          {{ $t('pages.home.homeType.explore.legendCdh') }}
        </span>
        <span class="inline-flex items-center gap-1.5">
          <span class="inline-block h-2 w-2 rotate-45 rounded-[1px]" :style="{ backgroundColor: 'var(--color-type-gite)' }" aria-hidden="true" />
          {{ $t('pages.home.homeType.explore.legendGite') }}
        </span>
      </div>

      <!-- État vide (mode filtré sans stock du type dans aucune région) -->
      <p
        v-if="regions.length === 0"
        class="max-w-[1120px] mx-auto py-10 text-center font-sans text-paper-900/60"
      >
        {{ $t('pages.home.homeType.explore.empty') }}
      </p>

      <ol v-else class="max-w-[1120px] mx-auto grid grid-cols-1 md:grid-cols-2 md:gap-x-10 lg:gap-x-16 border-t hairline">
        <li
          v-for="(region, i) in regions"
          :key="region.slug"
          class="group border-b hairline"
        >
          <NuxtLink
            :to="regionLink(region)"
            class="flex items-baseline gap-4 py-4 md:py-5 px-2 hover:bg-paper-100/40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-700 transition-colors"
          >
            <span class="font-mono text-[11px] text-paper-900/40 w-8 shrink-0 tabular-nums">
              {{ String(i + 1).padStart(2, '0') }}
            </span>
            <div class="flex-1 min-w-0">
              <h3 class="font-display text-[22px] md:text-[26px] leading-tight group-hover:italic">
                {{ region.name }}
              </h3>
              <p
                v-if="region.departmentSamples.length > 0"
                class="font-sans text-[13px] text-paper-900/50 mt-1 leading-snug"
              >
                {{ region.departmentSamples.join(' · ') }}
              </p>
            </div>

            <!-- Compteurs -->
            <template v-if="isAll">
              <!-- 2 compteurs colorés côte à côte. Doctrine « couleur jamais seule » :
                   chaque chiffre porte un 2e canal — la FORME (rond=cdh, losange=gîte),
                   identique au TypeBadge — en plus de la légende et de l'aria-label. -->
              <span
                class="inline-flex items-center gap-1 font-mono text-[12px] tabular-nums shrink-0"
                :style="{ color: 'var(--color-type-cdh)' }"
                :aria-label="`${region.cdhCount} ${$t('pages.home.homeType.explore.legendCdh')}`"
              >
                <span class="inline-block h-1.5 w-1.5 rounded-full" :style="{ backgroundColor: 'var(--color-type-cdh)' }" aria-hidden="true" />
                {{ region.cdhCount }}
              </span>
              <span
                v-if="region.giteCount > 0"
                class="inline-flex items-center gap-1 font-mono text-[12px] tabular-nums shrink-0"
                :style="{ color: 'var(--color-type-gite)' }"
                :aria-label="`${region.giteCount} ${$t('pages.home.homeType.explore.legendGite')}`"
              >
                <span class="inline-block h-1.5 w-1.5 rotate-45 rounded-[1px]" :style="{ backgroundColor: 'var(--color-type-gite)' }" aria-hidden="true" />
                {{ region.giteCount }}
              </span>
            </template>
            <span
              v-else
              class="font-mono text-[12px] tabular-nums shrink-0"
              :style="{ color: isGite ? 'var(--color-type-gite)' : 'var(--color-type-cdh)' }"
            >
              {{ filteredCount(region) }}
            </span>

            <UIcon
              name="i-lucide-arrow-right"
              class="size-4 text-paper-900/30 group-hover:translate-x-1 group-hover:text-paper-900/70 transition-all shrink-0"
            />
          </NuxtLink>
        </li>
      </ol>
    </div>
  </section>
</template>
